import { AuthFailureType, AuthStatus, init } from '@thoughtspot/visual-embed-sdk/lib/src/react';
import { useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { useBoundStore } from '@/store';
import { createEmbedConfig } from '@/lib/thoughtspot';
import { ThoughtSpotSdkAuthStatus } from '@/store/slices/createThoughtspotSlice';
import { captureEvent, captureException } from '@sentry/react';
import type { TSEventError } from '@/components/RootLayout';
import { getSdkTokenPromise } from '@/api/queries/useSdkTokenQuery';
import { getGetSdkTokenQueryKey } from '@/api/generated/endpoints/dashboard';
import { tokenStorage } from '@/lib/tokenStorage';

interface UseThoughtSpotAuthProps {
  setTSEventError: React.Dispatch<React.SetStateAction<TSEventError | null>>;
}

const SDK_AUTH_FAILURE = 'Clarity SDK authentication failed for user.';
const NO_COOKIE_ACCESS = 'Clarity SDK cannot access authentication cookies.';
const TOKEN_EXPIRED = 'Clarity SDK token has expired.';
const IDLE_SESSION_TIMEOUT = 'Clarity SDK session has timed out due to inactivity.';
const UNAUTHENTICATED_FAILURE = 'User is unauthenticated in Clarity SDK.';
const UNKNOWN_FAILURE = 'An unknown error occurred during Clarity SDK authentication.';

/**
 * Custom hook to handle authentication with ThoughtSpot SDK.
 * This hook initializes the ThoughtSpot SDK with the SDK token from React Query.
 * It also handles authentication success, failure, and user logout.
 *
 * @param setTSEventError - Function to set ThoughtSpot event errors
 * @returns void
 */
export const useThoughtSpotAuth = ({ setTSEventError }: UseThoughtSpotAuthProps) => {
  const { setAuthStatus } = useBoundStore();
  const { user, isAuthenticated } = useAuth0();
  const queryClient = useQueryClient();

  /**
   * Handle successful authentication with ThoughtSpot SDK.
   * Set the auth status to authenticated.
   * This function is called when the SDK successfully authenticates the user.
   * @returns void
   */
  const handleAuthSuccess = useCallback(() => {
    setAuthStatus(ThoughtSpotSdkAuthStatus.Authenticated);
    setTSEventError(null);
  }, [setAuthStatus, setTSEventError]);

  /**
   * Handle user logout.
   * Clear the SDK token from sessionStorage and set the auth status to unauthenticated.
   * This function is called when the user logs out.
   * @returns void
   * */
  const handleLogout = useCallback(() => {
    tokenStorage.clearToken();
    queryClient.removeQueries({ queryKey: getGetSdkTokenQueryKey() });
    setAuthStatus(ThoughtSpotSdkAuthStatus.Unauthenticated);
  }, [queryClient, setAuthStatus]);

  /**
   * Handle ThoughtSpot Authentication Failure
   * Uses Clarity to mask Thoughtspot branding in error messages.
   * This function is called when the SDK fails to authenticate the user.
   * @param errorType  - The type of authentication failure
   * @returns void
   */
  const handleAuthFailure = useCallback(
    async (errorType: AuthFailureType) => {
      const errorTypeName = AuthFailureType[errorType] ?? 'UNKNOWN';
      const userId = user?.sub;

      switch (errorType) {
        case AuthFailureType.SDK:
          setTSEventError({
            errorType: AuthFailureType.SDK,
            message: SDK_AUTH_FAILURE,
            status: 401,
          });

          console.log('ThoughtSpot SDK Authentication Failure:', {
            errorType: errorTypeName,
            host: thoughtspotHost,
          });

          captureEvent({
            message: SDK_AUTH_FAILURE,
            level: 'error',
            tags: {
              thoughtspot_auth_failure: errorTypeName,
            },
            extra: {
              userId,
            },
          });

          break;
        case AuthFailureType.NO_COOKIE_ACCESS:
          setTSEventError({
            errorType: AuthFailureType.NO_COOKIE_ACCESS,
            message: NO_COOKIE_ACCESS,
            status: 403,
          });

          captureEvent({
            message: NO_COOKIE_ACCESS,
            level: 'error',
            tags: {
              thoughtspot_auth_failure: errorTypeName,
            },
            extra: {
              userId,
            },
          });

          break;
        case AuthFailureType.EXPIRY:
          try {
            // Invalidate current token to mark it as stale
            await queryClient.invalidateQueries({
              queryKey: getGetSdkTokenQueryKey(),
            });

            // Use ensureQueryData to guarantee the query completes before reading result
            const result = await queryClient.ensureQueryData({
              queryKey: getGetSdkTokenQueryKey(),
              queryFn: async () => {
                const { getSdkToken } = await import('@/api/generated/endpoints/dashboard');
                return getSdkToken();
              },
            });

            if (result?.sdkToken) {
              // Update sessionStorage with fresh token
              tokenStorage.setToken(result);
              setTSEventError(null);
              setAuthStatus(ThoughtSpotSdkAuthStatus.Authenticated);
              return;
            }

            // If we reach here, refetch returned data but no token
            setTSEventError({
              errorType: AuthFailureType.EXPIRY,
              message: TOKEN_EXPIRED,
              status: 440,
            });

            captureEvent({
              message: `${TOKEN_EXPIRED} - No token in response`,
              level: 'error',
              tags: {
                thoughtspot_auth_failure: errorTypeName,
              },
              extra: {
                userId,
              },
            });
          } catch (error) {
            setTSEventError({
              errorType: AuthFailureType.EXPIRY,
              message: TOKEN_EXPIRED,
              status: 440,
            });

            captureEvent({
              message: `${TOKEN_EXPIRED} - Refetch failed`,
              level: 'error',
              tags: {
                thoughtspot_auth_failure: errorTypeName,
              },
              extra: {
                userId,
                error,
              },
            });
          }
          break;
        case AuthFailureType.IDLE_SESSION_TIMEOUT:
          setTSEventError({
            errorType: AuthFailureType.IDLE_SESSION_TIMEOUT,
            message: IDLE_SESSION_TIMEOUT,
            status: 440,
          });
          captureEvent({
            message: IDLE_SESSION_TIMEOUT,
            level: 'error',
            tags: {
              thoughtspot_auth_failure: errorTypeName,
            },
            extra: {
              userId,
            },
          });
          break;
        case AuthFailureType.UNAUTHENTICATED_FAILURE:
          console.log('ThoughtSpot SDK Authentication Failure:', {
            errorType: errorTypeName,
            host: thoughtspotHost,
          });
          setTSEventError({
            errorType: AuthFailureType.UNAUTHENTICATED_FAILURE,
            message: UNAUTHENTICATED_FAILURE,
            status: 401,
          });
          captureEvent({
            message: UNAUTHENTICATED_FAILURE,
            level: 'error',
            tags: {
              thoughtspot_auth_failure: errorTypeName,
            },
            extra: {
              userId,
            },
          });
          break;
        case AuthFailureType.OTHER:
          setTSEventError({
            errorType: AuthFailureType.OTHER,
            message: UNKNOWN_FAILURE,
            status: 500,
          });
          captureEvent({
            message: UNKNOWN_FAILURE,
            level: 'error',
            tags: {
              thoughtspot_auth_failure: errorTypeName,
            },
            extra: {
              userId,
            },
          });
          break;
      }
      setAuthStatus(ThoughtSpotSdkAuthStatus.Unauthenticated);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setAuthStatus, queryClient, setTSEventError, user?.sub],
  );

  /**
   * Initialize ThoughtSpot SDK once when user authenticates.
   *
   * Token Lifecycle:
   * - Token lifetime: 30 minutes (expires at T+30)
   * - React Query refetch: Every 20 minutes (background refresh at T+20, T+40, etc.)
   * - This provides a 10-minute safety buffer before token expiry
   * - The SDK's `getAuthToken` callback always returns the latest token from React Query cache
   *
   * Re-initialization should only occur when:
   * - User authentication status changes (login/logout)
   * - User object changes (different user)
   * - Event handlers need to be updated (callback ref changes)
   */

  const thoughtspotHost = useBoundStore((state) => state.getCurrentThoughtspotUrl());
  const isRegionTransitioning = useBoundStore((state) => state.isRegionTransitioning);

  useEffect(() => {
    if (!isAuthenticated) {
      setAuthStatus(ThoughtSpotSdkAuthStatus.Unauthenticated);
      return;
    }

    // Wait for region initialization to complete before checking host
    if (isRegionTransitioning) {
      return;
    }

    if (!thoughtspotHost) {
      captureException('useThoughtSpotAuth: thoughtSpotHost is missing.', {
        extra: { username: user?.name || user?.email || '' },
      });
      setTSEventError({
        errorType: AuthFailureType.OTHER, // Use OTHER for config errors
        message: 'Clarity SDK configuration is incomplete (missing host).',
        status: 500,
      });
      return;
    }

    const username = user?.name || user?.email || '';
    let config = createEmbedConfig({ username, host: thoughtspotHost });
    config = {
      ...config,
      // getAuthToken callback is invoked by SDK whenever it needs a token
      // This includes initial auth and automatic token refresh
      // getSdkTokenPromise checks sessionStorage → React Query cache → API
      getAuthToken: () => getSdkTokenPromise(queryClient),
    };

    const ThoughtSpotEventEmitter = init(config);

    if (ThoughtSpotEventEmitter) {
      ThoughtSpotEventEmitter.on(AuthStatus.SDK_SUCCESS, handleAuthSuccess);
      ThoughtSpotEventEmitter.on(AuthStatus.LOGOUT, handleLogout);
      ThoughtSpotEventEmitter.on(AuthStatus.FAILURE, handleAuthFailure);
    }

    return () => {
      if (ThoughtSpotEventEmitter) {
        ThoughtSpotEventEmitter.removeAllListeners(AuthStatus.SDK_SUCCESS);
        ThoughtSpotEventEmitter.removeAllListeners(AuthStatus.LOGOUT);
        ThoughtSpotEventEmitter.removeAllListeners(AuthStatus.FAILURE);
      }
    };
  }, [
    isAuthenticated,
    user,
    thoughtspotHost,
    isRegionTransitioning,
    // NOTE: sdkToken is intentionally NOT in dependencies - see comment above
    queryClient,
    handleAuthSuccess,
    handleLogout,
    setAuthStatus,
    setTSEventError,
    handleAuthFailure,
  ]);
  // Development-only listener for debug auth failure events dispatched by DebugTools
  useEffect(() => {
    if (!import.meta.env?.DEV) return;
    const debugHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { type?: AuthFailureType } | undefined;
      const failureType = detail?.type;
      if (failureType) {
        handleAuthFailure(failureType);
      }
    };
    window.addEventListener('debug-ts-auth-failure', debugHandler as EventListener);
    return () => {
      window.removeEventListener('debug-ts-auth-failure', debugHandler as EventListener);
    };
  }, [handleAuthFailure]);
};
